/**
 * Public engine API client. Three read/quote calls — catalogue, font binary,
 * validate-and-price — the P1 proof calls (the only way to the cart since 1.8.0),
 * the credits read, plus a boot-time contract-version assertion.
 *
 * Base URL: `VITE_ENGINE_API_BASE` in prod (the cross-origin api host); empty in
 * dev, where Vite proxies `/v1/engine/*` same-origin to the local backend.
 */

import {
    ENGINE_CONTRACT_VERSION,
    type ApprovalConfirmResult,
    type ApprovalRead,
    type ApprovalReportResult,
    type DesignCredits,
    type DesignRead,
    type Catalogue,
    type EngineErrorCode,
    type IconSearchResult,
    type ProofApproveResponse,
    type ProofStartResponse,
    type ProofStartWire,
    type ProofState,
    type ValidateAndPriceResponse,
} from './types.js';

import { withRetry } from './retry.js';

const BASE = import.meta.env.VITE_ENGINE_API_BASE ?? '';
const CONTRACT_HEADER = 'X-Engine-Contract';

/** A typed engine failure — carries the coarse code + correlation id for support. */
export class EngineError extends Error {
    constructor(
        readonly code: EngineErrorCode | 'network' | 'contract_mismatch',
        message: string,
        readonly status = 0,
        readonly requestId?: string,
    ) {
        super(message);
        this.name = 'EngineError';
    }
}

/** What a customer reads when a request never got a readable answer (Ken 10-26: the browser's own "Failed to fetch" means nothing to them). */
export const UNREACHABLE_COPY = 'We couldn’t reach our server just now. Check your connection and try again in a moment.';

/**
 * A request that never got a readable answer: offline, or blocked before the API (a response without CORS headers
 * reads the same). The customer gets plain words; the browser's own reason goes to the console for support.
 */
export function unreachable(what: string, cause: unknown): EngineError {
    console.warn(`[engine] couldn't reach the ${what}: ${(cause as Error)?.message ?? String(cause)}`);
    return new EngineError('network', UNREACHABLE_COPY);
}

export function url(path: string): string {
    return `${BASE}${path}`;
}

/** One fetch; no answer at all becomes the customer-worded `network` error. {@link withRetry} sends it again when it may. */
export async function send(target: string, init: RequestInit, what: string): Promise<Response> {
    try {
        return await fetch(target, init);
    } catch (e) {
        if (init.signal?.aborted === true) {
            throw new EngineError('network', 'Cancelled.'); // superseded on purpose (a newer quote): not a fault to log
        }
        throw unreachable(what, e);
    }
}

/**
 * Deploy-ordering guard (SOPHIA H1): the backend must ship an additive contract
 * bump before the SPA that depends on it.
 *
 * Compatibility is MAJOR-only, NOT exact MAJOR.MINOR (PRIYA P4): minor bumps are
 * additive by the contract's SemVer rules, so an SPA built against 1.3.x keeps
 * working against a 1.4.x backend — it simply ignores fields it doesn't know.
 * If this were exact-minor, the backend-first deploy window (backend already on
 * the new minor, the Cloudflare Pages SPA still rebuilding on the old one) would
 * hard-fail every engine call = a full self-inflicted outage on each minor
 * release. Only a MAJOR mismatch (a breaking change) or a missing header fails
 * loud. Backend is never BEHIND the SPA (deploy ordering), so "older backend
 * minor" isn't a case we have to defend.
 */
export function assertContract(res: Response): void {
    const got = res.headers.get(CONTRACT_HEADER);
    if (got === null) {
        throw new EngineError('contract_mismatch', 'Engine contract header missing.', res.status);
    }
    servedContract = got;
    const major = (v: string): string => v.split('.')[0];
    if (major(got) !== major(ENGINE_CONTRACT_VERSION)) {
        throw new EngineError(
            'contract_mismatch',
            `Engine contract ${got} is incompatible with this app (${ENGINE_CONTRACT_VERSION}).`,
            res.status,
        );
    }
}

/** The contract version the engine last answered with (null before the first call). */
let servedContract: string | null = null;

/**
 * Does the engine serve at least this contract version? A feature that needs new
 * routes (save + share, 1.11.0) shows only when it does (PRIYA 9), so a storefront
 * published ahead of the back end hides the feature instead of 404ing.
 */
/** The engine release that saves a carted design to My tokens (`saveToMyTokens` on approve). */
export const SAVE_ON_CART_CONTRACT = '1.14.0';
/** The engine release that takes a pack name on approve (`customerLabel`, shown as "Name: …" on the cart line; Ken 11-03). */
export const PACK_NAME_CONTRACT = '1.25.0';
/** The longest pack name the server takes, after its own cleanup. */
export const PACK_NAME_MAX = 40;
/** The engine release where an emptied name on Change quantity means "no name" (`customerLabel: ""`; Ken 11-03). */
export const PACK_NAME_CLEAR_CONTRACT = '1.28.0';

export function engineServesAtLeast(version: string): boolean {
    return servedContract !== null && compareVersions(servedContract, version) >= 0;
}

export function compareVersions(a: string, b: string): number {
    const pa = a.split('.').map((n) => Number.parseInt(n, 10) || 0);
    const pb = b.split('.').map((n) => Number.parseInt(n, 10) || 0);
    for (let i = 0; i < 3; i++) {
        const d = (pa[i] ?? 0) - (pb[i] ?? 0);
        if (d !== 0) {
            return d;
        }
    }
    return 0;
}

export async function asEngineError(res: Response): Promise<EngineError> {
    let code: EngineErrorCode = 'server_error';
    let message = `Request failed (${res.status}).`;
    let requestId: string | undefined;
    try {
        const body = (await res.json()) as { error?: { code?: EngineErrorCode; message?: string; requestId?: string } };
        if (body.error?.code) {
            code = body.error.code;
        }
        if (body.error?.message) {
            message = body.error.message;
        }
        requestId = body.error?.requestId;
    } catch {
        // non-JSON error body — keep the coarse defaults
    }
    return new EngineError(code, message, res.status, requestId);
}

export async function getCatalogue(
    customItemTypeKey: string,
    opts: { iconOffset?: number; iconLimit?: number } = {},
    turnstileToken?: string | null,
): Promise<Catalogue> {
    const params = new URLSearchParams({ customItemTypeKey });
    if (opts.iconOffset !== undefined) {
        params.set('iconOffset', String(opts.iconOffset));
    }
    if (opts.iconLimit !== undefined) {
        params.set('iconLimit', String(opts.iconLimit));
    }

    // The boot call carries the (optional) Turnstile token — the design-session
    // handshake. Backend verifies + logs it (report mode) or gates on it (the
    // dark enforce mode). Absent token is fine: the beta runs Turnstile off.
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (turnstileToken !== undefined && turnstileToken !== null && turnstileToken !== '') {
        headers['Cf-Turnstile-Response'] = turnstileToken;
    }

    const res = await withRetry('unkeyed', () => send(url(`/v1/engine/catalogue?${params}`), { headers }, 'design service'));
    if (!res.ok) {
        throw await asEngineError(res);
    }
    assertContract(res); // first successful call is the boot handshake
    return (await res.json()) as Catalogue;
}

/** The engine release whose catalogue draws each font's name, so fonts load only when used (Ken 10-28). */
export const FONT_LIBRARY_CONTRACT = '1.16.0';

/** The engine release that searches icons (`GET /v1/engine/icons`) and marks featured ones (Ken 10-28). */
export const ICON_SEARCH_CONTRACT = '1.15.0';

/** Icons whose name, key or tags contain `q` (2 to 64 characters), a page at a time. */
export async function searchIcons(
    customItemTypeKey: string,
    q: string,
    opts: { offset?: number; limit?: number; signal?: AbortSignal } = {},
): Promise<IconSearchResult> {
    const params = new URLSearchParams({ customItemTypeKey, q });
    if (opts.offset !== undefined) {
        params.set('offset', String(opts.offset));
    }
    if (opts.limit !== undefined) {
        params.set('limit', String(opts.limit));
    }
    const res = await withRetry('unkeyed', () => send(url(`/v1/engine/icons?${params}`), { headers: { Accept: 'application/json' }, signal: opts.signal }, 'icon search'), { signal: opts.signal });
    if (!res.ok) {
        throw await asEngineError(res);
    }
    assertContract(res);
    return (await res.json()) as IconSearchResult;
}

/** Content-versioned font URL (the sha256 busts the immutable cache on change). */
export function fontUrl(key: string, sha256: string): string {
    return url(`/v1/engine/fonts/${encodeURIComponent(key)}?v=${encodeURIComponent(sha256)}`);
}

export async function validateAndPrice(
    orderQuantity: number,
    designSpec: unknown,
    signal?: AbortSignal,
): Promise<ValidateAndPriceResponse> {
    const body = JSON.stringify({ orderQuantity, designSpec });
    const res = await withRetry('unkeyed', () => send(url('/v1/engine/validate-and-price'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body,
        signal,
    }, 'pricing service'), { signal });
    if (!res.ok) {
        throw await asEngineError(res);
    }
    // Assert the contract on the quote path too, not just boot (PRIYA R6): a
    // long-lived tab outliving a breaking deploy should fail loud, not mis-quote.
    assertContract(res);
    return (await res.json()) as ValidateAndPriceResponse;
}

// ── P1 proof (contract 1.8.0, docs/plans/tokenforge-p1b-build-spec.md) ──────────
//
// G (Ken, 10-20): the SPA never calls `POST /v1/engine/cart`; it goes 410
// `use_proofs` in the same release as these routes, so no client can skip the proof.

async function postJson<T>(path: string, body: unknown, idempotencyKey: string, what: string, bearer: string | null = null, approve = false): Promise<T> {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Idempotency-Key': idempotencyKey,
    };
    if (bearer !== null) {
        headers.Authorization = `Bearer ${bearer}`;
    }
    // Built ONCE: a retry sends these exact bytes under the same key (the server hashes the raw body).
    const bytes = JSON.stringify(body);
    const res = await withRetry('keyed', () => send(url(path), { method: 'POST', headers, body: bytes }, what), { approve });
    if (!res.ok) {
        throw await asEngineError(res);
    }
    assertContract(res);
    return (await res.json()) as T;
}

/**
 * A: freeze the design and render its proof (in the request when a render slot
 * is free, else by the minute sweep). This WRITES, so it runs once per explicit
 * click. On a validation finding the status block is all null, nothing is
 * frozen, and this returns `proof: null`. No Shopify product exists until
 * {@link approveProof}.
 *
 * `idempotencyKey`: one per attempt, reused for a retry of it; a different body
 * under the same key is a 422 `idempotency_key_reused`.
 *
 * `expectedTotalCents` (1.19.0, Ken 10-29): the total the customer was shown. If today's quote
 * differs, nothing is frozen and the answer is a `price_changed` finding with today's pricing.
 */
export async function startProof(
    orderQuantity: number,
    designSpec: unknown,
    idempotencyKey: string,
    expectedTotalCents: number | null = null,
    previousDesignToken: string | null = null,
): Promise<ProofStartResponse> {
    // B3 (1.33.0): a re-proof of a mixed pack names its last proofed version, so `designs[].changedSinceLastProof` is set.
    const body = withExpectedTotal(previousDesignToken === null ? { orderQuantity, designSpec } : { orderQuantity, designSpec, previousDesignToken }, expectedTotalCents);
    return proofStartFrom(await postJson<ProofStartWire>('/v1/engine/proofs', body, idempotencyKey, 'proof service'));
}

/** Adds 1.19.0 `expectedTotalCents` to a proof request when the designer showed a total (a whole, non-negative number). */
export function withExpectedTotal<T extends Record<string, unknown>>(body: T, expectedTotalCents: number | null): T & { expectedTotalCents?: number } {
    return expectedTotalCents !== null && Number.isInteger(expectedTotalCents) && expectedTotalCents >= 0 ? { ...body, expectedTotalCents } : body;
}

/** A's answer, the same for a designSpec start and a saved revision's (1.11.0 reorder). */
export function proofStartFrom(wire: ProofStartWire): ProofStartResponse {
    const { validation, pricing, ...block } = wire;
    if (!validation.ok || block.designToken === null || block.status === null) {
        return { validation, pricing, proof: null };
    }
    return { validation, pricing, proof: withAbsoluteProofUrl(block as ProofState) };
}

/** B: cheap status read; the SPA polls it until `ready`, `failed` or the wait budget runs out. */
export async function pollProof(designToken: string): Promise<ProofState> {
    const res = await withRetry('unkeyed', () => send(url(`/v1/engine/proofs/${encodeURIComponent(designToken)}`), {
        headers: { Accept: 'application/json' },
    }, 'proof service'));
    if (!res.ok) {
        throw await asEngineError(res);
    }
    assertContract(res);
    return withAbsoluteProofUrl((await res.json()) as ProofState);
}

/**
 * The proof image is shown in an <img> on the storefront's own origin, so a
 * relative `/v1/engine/...` path would resolve against design.litko.net. The
 * engine sends absolute URLs; this is the belt to that.
 */
function withAbsoluteProofUrl(proof: ProofState): ProofState {
    return proof.proofUrl !== null && proof.proofUrl.startsWith('/')
        ? { ...proof, proofUrl: url(proof.proofUrl) }
        : proof;
}

/**
 * D: approve the proof and make the cart product. `proofSha256` is the hash of
 * the image the customer saw, or null when adding to cart while it is pending.
 * `acknowledgementVersion` names the approval wording the customer ticked; the
 * server stores it with the approval. 409s the caller handles: `proof_not_ready`,
 * `price_expired`, `proof_failed`, `proof_mismatch`.
 */
export function approveProof(
    designToken: string,
    proofSha256: string | null,
    acknowledgementVersion: string,
    idempotencyKey: string,
    replacesDesignToken: string | null = null,
    notifyEmail: string | null = null,
    canvasShaper: string | null = null,
    saveWithOwnerKey: string | null = null,
    customerLabel: string | null = null,
    manifestSha256: string | null = null,
): Promise<ProofApproveResponse> {
    // `replacesDesignToken` (change quantity): the server answers with a cart URL
    // that swaps the old pack for the new one, or the plain add if it no longer
    // qualifies — never a refusal. `notifyEmail`: required on a pending approve
    // (where the approval link is sent), never sent on a ready one. `canvasShaper`
    // (1.9.0): which shaper build drew the canvas, sent only on a pending approve;
    // the server waits for the drawn proof when it isn't its own build.
    // B3: a paged (1.2) proof is approved by its manifest, which names every page in order; proofSha256 is then left out.
    const body: Record<string, unknown> = manifestSha256 !== null
        ? { manifestSha256, acknowledgementVersion }
        : { proofSha256, acknowledgementVersion };
    if (replacesDesignToken !== null) {
        body.replacesDesignToken = replacesDesignToken;
    }
    if (notifyEmail !== null && proofSha256 === null) {
        body.notifyEmail = notifyEmail;
    }
    if (canvasShaper !== null && proofSha256 === null) {
        body.canvasShaper = canvasShaper;
    }
    // 1.14.0 (Ken 10-26): with this browser's owner key, the server also saves the design to
    // My tokens, best effort and once per design; the cart never depends on it.
    // Only to an engine that knows the field: an older one could refuse the unknown key, and the cart must never fail for this.
    const saveWith = saveWithOwnerKey !== null && engineServesAtLeast(SAVE_ON_CART_CONTRACT) ? saveWithOwnerKey : null;
    if (saveWith !== null) {
        body.saveToMyTokens = true;
    }
    // 1.25.0: the pack's name for the cart line. The server cleans it and keeps the FIRST one sent for this pack.
    const label = customerLabel?.trim() ?? '';
    if (label !== '' && engineServesAtLeast(PACK_NAME_CONTRACT)) {
        body.customerLabel = label;
    } else if (label === '' && replacesDesignToken !== null && engineServesAtLeast(PACK_NAME_CLEAR_CONTRACT)) {
        // Change quantity with the box emptied: the customer took the name off, so the old cart line's name must not carry over.
        body.customerLabel = '';
    }
    return postJson(
        `/v1/engine/proofs/${encodeURIComponent(designToken)}/approve`,
        body,
        idempotencyKey,
        'cart service',
        saveWith,
        true,
    );
}

/** E: read a pack back to reopen it (change quantity). 404 = unknown, paid, or past its 30 days. */
export async function getDesign(designToken: string): Promise<DesignRead> {
    const res = await withRetry('unkeyed', () => send(url(`/v1/engine/designs/${encodeURIComponent(designToken)}`), {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
    }, 'design service'));
    if (!res.ok) {
        throw await asEngineError(res);
    }
    assertContract(res);
    return (await res.json()) as DesignRead;
}

// ── per-order credits (R17, contract 1.6.0) ─────────────────────────────────────

/** The icon and font credits frozen with a design at add to cart. 404 = unknown token. */
export async function getDesignCredits(designToken: string): Promise<DesignCredits> {
    const res = await withRetry('unkeyed', () => send(url(`/v1/engine/credits/${encodeURIComponent(designToken)}`), {
        headers: { Accept: 'application/json' },
    }, 'credits service'));
    if (!res.ok) {
        throw await asEngineError(res);
    }
    assertContract(res);
    return (await res.json()) as DesignCredits;
}

// ── late-proof approval page (Ken, 10-20; contract 1.8.0) ───────────────────────
//
// The secret lives in the page's URL fragment and travels only in these POST
// bodies, never in a URL, so no server, proxy or Referer ever logs it. All three
// routes stay up when the engine is switched off: a paid pack waiting on its
// approval must never be stranded by an incident.

async function postApproval<T>(path: string, body: unknown): Promise<T> {
    // No Idempotency-Key on these routes: sent again only on `deploying` (approvals/report never after an ambiguous failure).
    const bytes = JSON.stringify(body);
    const res = await withRetry('unkeyed', () => send(url(path), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: bytes,
        cache: 'no-store',
        referrerPolicy: 'no-referrer',
    }, 'approval service'));
    if (!res.ok) {
        throw await asEngineError(res);
    }
    assertContract(res);
    return (await res.json()) as T;
}

/** What the approval page shows. Writes nothing and counts as nothing (a mail scanner may call it). */
export async function readApproval(secret: string): Promise<ApprovalRead> {
    const read = await postApproval<ApprovalRead>('/v1/engine/approvals/read', { secret });
    return {
        ...read,
        packs: read.packs.map((p) => (p.proofUrl !== null && p.proofUrl.startsWith('/') ? { ...p, proofUrl: url(p.proofUrl) } : p)),
    };
}

/** The customer's approval of one pack's drawn proof. Set once; a repeat answers the stored result. */
export function confirmApproval(
    secret: string,
    designToken: string,
    proofSha256: string,
    acknowledgementVersion: string,
): Promise<ApprovalConfirmResult> {
    return postApproval('/v1/engine/approvals/confirm', { secret, designToken, proofSha256, acknowledgementVersion });
}

/** "Something's wrong" for one pack: staff follow up and its reminders stop. */
export function reportApproval(secret: string, designToken: string, message: string): Promise<ApprovalReportResult> {
    const trimmed = message.trim();
    return postApproval('/v1/engine/approvals/report', trimmed === '' ? { secret, designToken } : { secret, designToken, message: trimmed });
}
